// Hook: lit un cookie du navigateur et le pousse dans un champ React Hook Form
// Usage:
//   import { FormProvider, useForm } from 'react-hook-form'
//   import { useCookieField } from 'hooks'
//   const methods = useForm({ defaultValues: { lead: { ga: '' } } })
//   <FormProvider {...methods}>
//     <MyComponent />
//   </FormProvider>
//   function MyComponent() {
//     useCookieField({ cookieName: '_ga', fieldName: 'lead.ga' })
//     return null
//   }

import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

/**
 * Lit la valeur d'un cookie (si disponible côté navigateur)
 * @param {string} name
 * @returns {string|undefined}
 */
function readCookie(name) {
  if (typeof document === 'undefined') return undefined
  const cookieString = `; ${document.cookie || ''}`
  const parts = cookieString.split(`; ${name}=`)
  if (parts.length < 2) return undefined
  return parts.pop().split(';').shift()
}

/**
 * Pousse la valeur d'un cookie dans un champ React Hook Form.
 * @param {Object} params
 * @param {string} params.cookieName - Nom du cookie à lire.
 * @param {string} params.fieldName - Nom du champ (chemin dot-notation ex: "lead.GA__c").
 * @param {boolean} [params.setIfEmptyOnly=true] - N'écrase que si le champ est vide.
 * @param {(v:string)=>any} [params.transform] - Transforme la valeur du cookie avant setValue.
 * @param {boolean} [params.disabled=false] - Désactive le hook.
 */
export default function useCookieField({
  cookieName,
  fieldName,
  setIfEmptyOnly = true,
  transform,
  disabled = false
}) {
  const ctx = useFormContext?.()

  useEffect(() => {
    if (disabled) return
    if (!ctx || !ctx.setValue || !ctx.getValues) return
    if (!cookieName || !fieldName) return

    const raw = readCookie(cookieName)
    if (raw == null) return

    const current = ctx.getValues(fieldName)
    const isEmpty = current === undefined || current === null || current === ''
    if (setIfEmptyOnly && !isEmpty) return

    const next = typeof transform === 'function' ? transform(raw) : raw
    // On ne marque pas dirty/validate par défaut pour ne pas perturber le formulaire
    ctx.setValue(fieldName, next, { shouldValidate: false, shouldDirty: false })
  }, [cookieName, fieldName, setIfEmptyOnly, transform, disabled, ctx])
}

export { readCookie }
